import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { FitnessRecord, KidTabProps } from "./types";

type FitnessData = { fitnessRecords: FitnessRecord[] };

interface Props extends KidTabProps<FitnessData> {}

// 'up' = higher value is better, 'down' = lower value is better, 'neutral' = no delta arrow
type MetricDirection = "up" | "down" | "neutral";

interface MetricDef {
  key: keyof FitnessRecord;
  label: string;
  unit: string;
  direction: MetricDirection;
}

const METRICS: MetricDef[] = [
  { key: "weightKg",       label: "Weight",         unit: "kg",  direction: "neutral" },
  { key: "heightCm",       label: "Height",         unit: "cm",  direction: "neutral" },
  { key: "bmi",            label: "BMI",            unit: "",    direction: "neutral" },
  { key: "yoYoLevel",      label: "Yo-Yo Level",   unit: "",    direction: "up"      },
  { key: "pushUps",        label: "Push-Ups",       unit: "reps",direction: "up"      },
  { key: "shuttleRunSec",  label: "Shuttle Run",    unit: "sec", direction: "down"    },
];

function deltaDisplay(
  current: number | null,
  prior: number | null,
  direction: MetricDirection,
) {
  if (current == null || prior == null || direction === "neutral") return null;
  const diff = current - prior;
  if (Math.abs(diff) < 0.01) return { icon: <Minus className="h-3.5 w-3.5" />, color: "text-gray-400", label: "0" };
  const improved = direction === "up" ? diff > 0 : diff < 0;
  const sign = diff > 0 ? "+" : "";
  return {
    icon: improved
      ? <TrendingUp className="h-3.5 w-3.5" />
      : <TrendingDown className="h-3.5 w-3.5" />,
    color: improved ? "text-green-600" : "text-red-500",
    label: `${sign}${diff.toFixed(1)}`,
  };
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function NarrativeSlot({ narrative }: { narrative: Props["narrative"] }) {
  if (!narrative) return null;
  return (
    <div className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-3 text-sm text-teal-800 mb-5">
      {narrative}
    </div>
  );
}

export function KidFitnessTab({ data, isLoading, isEmpty, error, onRetry, narrative }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="text-sm text-gray-500">{error}</p>
        {onRetry && <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>}
      </div>
    );
  }

  if (isEmpty || data.fitnessRecords.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center text-gray-400">
        <p className="font-medium text-gray-500">No fitness records yet</p>
        <p className="text-sm mt-1">Records will appear after the first fitness assessment.</p>
      </div>
    );
  }

  // newest first — pair each record with the one after it as "prior"
  const records = [...data.fitnessRecords].sort(
    (a, b) => new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime()
  );

  return (
    <div className="space-y-5 py-2" data-testid="tab-content-fitness-loaded">
      <NarrativeSlot narrative={narrative} />

      {records.map((record, i) => {
        const prior = records[i + 1] ?? null;
        const isLatest = i === 0;

        return (
          <div
            key={record.measuredAt}
            className="bg-amber-50 border border-amber-100 rounded-xl p-5 shadow-sm"
          >
            {/* Card header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-semibold tracking-wide uppercase text-amber-600">
                  {isLatest ? "Latest Assessment" : "Prior Assessment"}
                </p>
                <p className="text-sm font-medium text-amber-900 mt-0.5">
                  {formatDate(record.measuredAt)}
                </p>
              </div>
              {isLatest && (
                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                  Current
                </span>
              )}
            </div>

            {/* Metric grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {METRICS.map(({ key, label, unit, direction }) => {
                const current = record[key] as number | null;
                const priorVal = prior ? (prior[key] as number | null) : null;
                const delta = isLatest ? deltaDisplay(current, priorVal, direction) : null;

                return (
                  <div
                    key={key}
                    className="bg-white rounded-lg px-3 py-3 border border-amber-100"
                  >
                    <p className="text-xs text-amber-700 mb-1">{label}</p>
                    <div className="flex items-end gap-1.5">
                      <span className="text-lg font-bold text-gray-900">
                        {current != null ? current : "—"}
                      </span>
                      {unit && <span className="text-xs text-gray-400 mb-0.5">{unit}</span>}
                    </div>
                    {delta && (
                      <div className={`flex items-center gap-0.5 mt-1 text-xs font-medium ${delta.color}`}>
                        {delta.icon}
                        <span>{delta.label} vs prior</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
